/**
 * Facebook Pages Adapter (Story 9.2, IMC-7, IMC-22)
 *
 * Plain object implementing IntegrationAdapter — no class, no inheritance.
 * Uses safeFetch() for all external API calls.
 *
 * CRITICAL: Page Insights API requires the Page Access Token (not the user token).
 * The adapter IGNORES credentials.accessToken. Normal product calls retrieve
 * the Page Access Token from KV via getPageAccessToken(); live-evidence
 * certification receives only a server-bound token resolver capability.
 *
 * Uses Meta Graph API v25.0 metrics — deprecated metrics (page_impressions,
 * page_fans, etc.) are NOT used. See Dev Notes for full deprecation map.
 *
 * ADR-013: Adapter Pattern for API Isolation
 */

import type {
  DimensionDefinition,
  IntegrationAdapter,
  AdapterContext,
  OAuthCredentials,
  AdapterResult,
  AdapterMetrics,
  AccountInfo,
  MetricDefinition,
} from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { META_GRAPH_API_VERSION, META_INTEGRATION_SCOPES } from "./scopes";
import {
  discoverMetaAccounts,
  parseAppUsage,
  type MetaDiscoveryFirstError,
  type MetaSectionStatus,
} from "./discovery";
import { getPageAccessToken,
  type CertificationPageAccessTokenResolver,
} from "./page-tokens";
import { parseMetaGraphError } from "./errors";

// ============================================================================
// Constants
// ============================================================================

const META_GRAPH_BASE = `https://graph.facebook.com/${META_GRAPH_API_VERSION}`;

/** Rate limit threshold — stop making calls if x-app-usage call_count > 80% */
const RATE_LIMIT_THRESHOLD = 80;

/** Maximum data retention for Facebook Pages (2 years) */
const MAX_RETENTION_DAYS = 730;

const CERTIFICATION_LOG_IDENTIFIER_KEYS = [
  "tenantId",
  "pageId",
  "postId",
] as const;

function logFacebookMetricsEvent(
  ctx: AdapterContext,
  payload: Readonly<Record<string, unknown>>,
): void {
  if (ctx.certification?.mode !== "live-evidence") {
    console.log(JSON.stringify(payload));
    return;
  }

  const safePayload: Record<string, unknown> = { ...payload };
  for (const key of CERTIFICATION_LOG_IDENTIFIER_KEYS) {
    delete safePayload[key];
  }
  console.log(JSON.stringify(safePayload));
}

// ============================================================================
// Types (local to adapter, NOT exported)
// ============================================================================

interface MetaInsightsResponse {
  data?: Array<{
    name: string;
    period: string;
    values: Array<{
      value: number | Record<string, number>;
      end_time: string;
    }>;
  }>;
  error?: {
    message: string;
    type: string;
    code: number;
    error_subcode?: number;
  };
}

interface MetaPostInsightsResponse {
  data?: Array<{
    name: string;
    period: string;
    values: Array<{
      value: number | Record<string, number>;
    }>;
  }>;
  error?: {
    message: string;
    type: string;
    code: number;
  };
}

interface MetaPostsResponse {
  data?: Array<{
    id: string;
    message?: string;
    created_time: string;
    full_picture?: string;
    permalink_url?: string;
    type?: string;
    shares?: { count: number };
    reactions?: { summary: { total_count: number } };
    comments?: { summary: { total_count: number } };
  }>;
  paging?: {
    next?: string;
  };
  error?: {
    message: string;
    type: string;
    code: number;
  };
}

// ============================================================================
// Meta Error Response Handler
// ============================================================================

/**
 * Attempts to parse a Meta error from an HTTP error response.
 * If the response body contains a recognized Meta error.code, returns an
 * AdapterResult with the correctly mapped error code.
 * Otherwise returns null to let safeFetch()'s default mapping apply.
 */
async function handleMetaFetchError(
  fetchResult: { success: false; error: { code: string; message: string; retryable: boolean; providerStatus?: number; providerMessage?: string;
  };
}
): Promise<AdapterResult<never>> {
  // Try to parse Meta error code from providerMessage (safeFetch extracts response body there)
  if (fetchResult.error.providerMessage) {
    const metaError = parseMetaGraphError(fetchResult.error.providerMessage);
    if (metaError) {
      return adapterErr({
        code: metaError.code,
        message: sanitizeErrorMessage(metaError.message),
        retryable: metaError.retryable,
        providerStatus: fetchResult.error.providerStatus,
      });
    }
  }

  // Fall back to safeFetch default mapping
  return adapterErr(fetchResult.error as Parameters<typeof adapterErr>[0]);
}

/**
 * Maps a non-complete catalog-discovery section outcome to an AdapterResult
 * error (Story 31.20 AC #3). Strict full-catalog semantics: a truncated
 * catalog is NEVER adapterOk. `rate_limited` is special-cased retryable;
 * otherwise the section's firstError flows through the Meta error path so a
 * revoked token surfaces as token_expired (Graph error 190) rather than a
 * blanket code. (Private copy — mirrors this adapter's private
 * handleMetaFetchError; facebook-ads exports the shared variant.)
 */
async function mapDiscoverySectionFailure(
  status: Exclude<MetaSectionStatus, "complete">,
  firstError: MetaDiscoveryFirstError | undefined,
): Promise<AdapterResult<never>> {
  if (status === "rate_limited") {
    return adapterErr({
      code: "rate_limited",
      message: "Meta account discovery stopped: app-level rate limit threshold reached",
      retryable: true,
    });
  }
  if (firstError) {
    return handleMetaFetchError({ success: false, error: firstError });
  }
  if (status === "aborted") {
    return adapterErr({
      code: "network_error",
      message: "Meta account discovery aborted before the full catalog was fetched",
      retryable: true,
    });
  }
  return adapterErr({
    code: "invalid_response",
    message: `Meta account discovery did not complete the required section (status: ${status})`,
    retryable: false,
  });
}

// ============================================================================
// Metric Definitions (Subtask 1.2)
// ============================================================================

const FB_PAGES_METRICS: MetricDefinition[] = [
  { key: "page_follows", label: "Followers", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  { key: "page_daily_follows", label: "New Followers", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  { key: "page_daily_unfollows", label: "Unfollows", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["line-chart"],
  },
  { key: "page_media_view", label: "Media Views", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  { key: "page_total_media_view_unique", label: "Unique Viewers", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  { key: "page_views_total", label: "Page Views", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  { key: "page_post_engagements", label: "Post Engagements", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  { key: "page_video_views", label: "Video Views", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"],
  },
  { key: "engagement_rate", label: "Engagement Rate", type: "percentage", category: "Performance", dataType: "Percentage", aggregationKind: "ratio", compatibleWidgets: ["kpi", "gauge"],
  },
  // Page-level reaction metrics (day period)
  { key: "page_actions_post_reactions_like_total", label: "Like Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart"],
  },
  { key: "page_actions_post_reactions_love_total", label: "Love Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart"],
  },
  { key: "page_actions_post_reactions_wow_total", label: "Wow Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart"],
  },
  { key: "page_actions_post_reactions_haha_total", label: "Haha Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart"],
  },
  { key: "page_actions_post_reactions_sorry_total", label: "Sad Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart"],
  },
  { key: "page_actions_post_reactions_anger_total", label: "Angry Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart"],
  },
  { key: "page_actions_post_reactions_total", label: "Total Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart"],
  },
  { key: "page_total_actions", label: "Total Page Actions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  // Page-level video metric splits
  { key: "page_video_views_paid", label: "Video Views (Paid)", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  { key: "page_video_views_organic", label: "Video Views (Organic)", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  { key: "page_video_complete_views_30s", label: "30s Complete Views", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"],
  },
  { key: "page_video_view_time", label: "Video View Time (ms)", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"],
  },
  { key: "page_video_views_autoplayed", label: "Video Views (Autoplayed)", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"],
  },
  { key: "page_video_views_click_to_play", label: "Video Views (Click to Play)", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"],
  },
  { key: "page_video_complete_views_30s_paid", label: "30s Views (Paid)", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"],
  },
  { key: "page_video_complete_views_30s_organic", label: "30s Views (Organic)", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"],
  },
  { key: "page_video_complete_views_30s_autoplayed", label: "30s Views (Autoplayed)", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"],
  },
  { key: "page_video_complete_views_30s_click_to_play", label: "30s Views (Click to Play)", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"],
  },
  { key: "page_video_repeat_views", label: "Repeat Video Views", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"],
  },
  // Follower split
  { key: "page_fan_adds_by_paid_non_paid_unique", label: "Follower Adds (Paid/Organic)", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart"],
  },
  { key: "page_lifetime_engaged_followers_unique", label: "Lifetime Engaged Followers", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"],
  },
  // Post-level metrics (from per-post insights)
  { key: "post_views", label: "Post Views", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
  { key: "post_unique_viewers", label: "Post Unique Viewers", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
  { key: "post_clicks", label: "Post Clicks", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
  // Post-level breakdown metrics (flattened from compound objects)
  { key: "post_clicks_link", label: "Post Link Clicks", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
  { key: "post_clicks_photo_view", label: "Post Photo Clicks", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
  { key: "post_clicks_other", label: "Post Other Clicks", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
  { key: "post_reactions_like", label: "Post Like Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
  { key: "post_reactions_love", label: "Post Love Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
  { key: "post_reactions_wow", label: "Post Wow Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
  { key: "post_reactions_haha", label: "Post Haha Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
  { key: "post_reactions_sorry", label: "Post Sad Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
  { key: "post_reactions_anger", label: "Post Angry Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
  { key: "post_activity_like", label: "Post Likes (Activity)", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
  { key: "post_activity_comment", label: "Post Comments (Activity)", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
  { key: "post_activity_share", label: "Post Shares (Activity)", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
  // Page-level derived totals (aggregated from post data)
  { key: "comments_total", label: "Total Comments", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"],
  },
  { key: "shares_total", label: "Total Shares", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"],
  },
  // Page-level paid vs organic video split (combined breakdown)
  { key: "page_video_views_by_paid_non_paid", label: "Video Views (Paid vs Organic)", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["bar-chart", "pie-chart"],
  },
  { key: "post_video_views", label: "Post Video Views", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
  { key: "post_video_30s_complete", label: "Post 30s Complete", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
  { key: "post_video_avg_watch_time", label: "Post Avg Watch Time", type: "number", category: "Content", dataType: "Duration", aggregationKind: "ratio", ratioComponents: { denominator: "post_video_views" }, compatibleWidgets: ["kpi", "data-table"],
  },
  { key: "post_video_view_time", label: "Post Video View Time", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
  { key: "post_views_paid", label: "Post Views (Paid)", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
  { key: "post_views_organic", label: "Post Views (Organic)", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
  { key: "post_views_follower", label: "Post Views (Followers)", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"],
  },
];

// ============================================================================
// Dimension Definitions (Subtask 1.3)
// ============================================================================

const FB_PAGES_DIMENSIONS: DimensionDefinition[] = [
  { key: "date", label: "Date", type: "Time", exampleValues: ["2026-03-01", "2026-03-02"],
  },
  { key: "day_of_week", label: "Day of Week", type: "Time", exampleValues: ["Monday", "Tuesday"],
  },
  { key: "post_type", label: "Post Type", type: "Content", exampleValues: ["photo", "video", "link", "status", "reel"],
  },
  // media_view breakdowns — aggregate on the metric, not per-day sliceable
  { key: "media_view_source", label: "Media View Source", type: "Content", exampleValues: ["ads", "followers", "organic"],
  },
];

// ============================================================================
// Insights Metrics (for the /{page-id}/insights API call)
// ============================================================================

/**
 * Page-level metrics fetched with period=day (time-series).
 * Split into two batches to stay under Meta's ~25 metrics per-request limit.
 */
const DAY_PERIOD_METRICS_BATCH_1 = [
  "page_media_view",
  "page_total_media_view_unique",
  "page_views_total",
  "page_post_engagements",
  "page_video_views",
  "page_daily_follows",
  "page_daily_unfollows",
  // Reaction metrics (day period)
  "page_actions_post_reactions_like_total",
  "page_actions_post_reactions_love_total",
  "page_actions_post_reactions_wow_total",
  "page_actions_post_reactions_haha_total",
  "page_actions_post_reactions_sorry_total",
  "page_actions_post_reactions_anger_total",
  "page_actions_post_reactions_total",
  "page_total_actions",
  // Follower paid/organic split
  "page_fan_adds_by_paid_non_paid_unique",
]; // 16 metrics

const DAY_PERIOD_METRICS_BATCH_2 = [
  // Video metric splits
  "page_video_views_paid",
  "page_video_views_organic",
  "page_video_views_autoplayed",
  "page_video_views_click_to_play",
  "page_video_complete_views_30s",
  "page_video_complete_views_30s_paid",
  "page_video_complete_views_30s_organic",
  "page_video_complete_views_30s_autoplayed",
  "page_video_complete_views_30s_click_to_play",
  "page_video_repeat_views",
  "page_video_view_time",
  // Paid vs organic combined breakdown (returns {paid: N, unpaid: N} — parseInsightsResponse sums to total)
  "page_video_views_by_paid_non_paid",
]; // 12 metrics

/** Page-level metrics fetched with period=day (cumulative / snapshot) */
const LIFETIME_METRICS = [
  "page_follows",
  "page_lifetime_engaged_followers_unique",
];

// ============================================================================
// Helper: Date Range Clamping
// ============================================================================

/**
 * Clamps the start date to max 2 years ago (Facebook data retention limit).
 * Returns unix timestamps (seconds) for the API call.
 * Adds 1 day to end date because Facebook's `until` param is non-inclusive.
 */
function clampAndConvertDateRange(start: Date, end: Date,
): { since: number; until: number; clamped: boolean;
  effectiveStart: Date;
} {
  const twoYearsAgo = new Date();
  twoYearsAgo.setUTCDate(twoYearsAgo.getUTCDate() - MAX_RETENTION_DAYS);

  let clamped = false;
  let effectiveStart = start;
  if (start < twoYearsAgo) {
    effectiveStart = twoYearsAgo;
    clamped = true;
  }

  // `until` is non-inclusive — add 1 day
  const untilDate = new Date(end);
  untilDate.setUTCDate(untilDate.getUTCDate() + 1);

  const since = Math.floor(effectiveStart.getTime() / 1000);
  return {
    since,
    until: Math.floor(untilDate.getTime() / 1000),
    clamped,
    // Preserve existing non-clamped semantics. For a clamped request, match
    // the exact second-resolution instant sent to the Graph API so
    // certification reports the provider window that was exercised.
    effectiveStart: clamped ? new Date(since * 1000) : start,
  };
}

// ============================================================================
// Helper: Parse Insights Response to Daily Records
// ============================================================================

function parseInsightsResponse(
  data: MetaInsightsResponse["data"],
): Map<string, Record<string, number>> {
  const dailyMap = new Map<string, Record<string, number>>();

  if (!data) return dailyMap;

  for (const metric of data) {
    for (const val of metric.values) {
      // end_time is in ISO format: "2026-03-02T08:00:00+0000"
      // For period=day the value date IS the end_time day.
      const dateStr = val.end_time.split("T")[0];
      if (!dateStr) continue;

      if (!dailyMap.has(dateStr)) {
        dailyMap.set(dateStr, {});
      }
      const record = dailyMap.get(dateStr)!;
      // Some metrics return objects (e.g. page_fan_adds_by_paid_non_paid_unique)
      // For those, store the numeric total or sum of values
      if (typeof val.value === "number") {
        record[metric.name] = val.value;
      } else if (typeof val.value === "object" && val.value !== null) {
        // Sum all values in the object for a numeric aggregate
        const sum = Object.values(val.value).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0,
        );
        record[metric.name] = sum;
      }
    }
  }

  return dailyMap;
}

// ============================================================================
// Helper: Day of Week
// ============================================================================

const DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

function getDayOfWeek(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00Z");
  return DAYS_OF_WEEK[d.getUTCDay()] ?? "Unknown";
}

// ============================================================================
// Facebook Pages Adapter (Subtask 1.1)
// ============================================================================

export const facebookPagesAdapter: IntegrationAdapter = {
  platform: "facebook-pages",

  rateLimits: {
    requestsPerMinute: 3,
    requestsPerDay: 4800,
  },

  // --------------------------------------------------------------------------
  // fetchMetrics (Subtask 1.4)
  // --------------------------------------------------------------------------
  async fetchMetrics(
    ctx: AdapterContext,
    _credentials: OAuthCredentials, // IGNORED — uses Page Access Token from KV
    dateRange: { start: Date; end: Date },
  ): Promise<AdapterResult<AdapterMetrics>> {
    const pageId = ctx.accountId;
    if (!pageId) {
      return adapterErr({
        code: "account_not_found",
        message: "No Facebook Page selected. Please select a Page first.",
        retryable: false,
      });
    }

    // Normal product calls retain the legacy KV + token ID + encryption-key
    // path. Certification is intentionally different: the server binds those
    // sensitive inputs to an already validated tenant/token/page and exposes a
    // zero-argument capability that returns only the selected decrypted token.
    const env = ctx.env as {
      KV: unknown;
      DB: unknown;
      ENCRYPTION_KEY?: string;
      oauthTokenId?: string;
      resolveCertificationPageAccessToken?: CertificationPageAccessTokenResolver;
    };

    let pageAccessToken: string;
    if (ctx.certification?.mode === "live-evidence") {
      if (!env.resolveCertificationPageAccessToken) {
        return adapterErr({
          code: "invalid_response",
          message: "Facebook Pages certification token capability not provided",
          retryable: false,
        });
      }

      const tokenResult = await env.resolveCertificationPageAccessToken();
      if (!tokenResult.success) {
        return adapterErr(tokenResult.error);
      }
      pageAccessToken = tokenResult.data.pageAccessToken;
    } else {
      if (!env.oauthTokenId) {
      return adapterErr({
        code: "invalid_response",
        message: "oauthTokenId not provided in adapter context",
        retryable: false,
      });
    }

    if (!env.ENCRYPTION_KEY) {
      return adapterErr({
        code: "invalid_response",
        message: "ENCRYPTION_KEY not provided in adapter context",
        retryable: false,
      });
    }

      // Retrieve and decrypt Page Access Token from KV.
      const tokenResult = await getPageAccessToken(
      env.KV,
      env.oauthTokenId,
      pageId,
      ctx.tenantId,
      env.ENCRYPTION_KEY,
      );

    if (!tokenResult.success) {
      return adapterErr(tokenResult.error);
    }
      pageAccessToken = tokenResult.data.pageAccessToken;
    }

    // Clamp date range and convert to Unix timestamps
    const { since, until, clamped, effectiveStart } = clampAndConvertDateRange(dateRange.start, dateRange.end,
    );

    if (effectiveStart.getTime() > dateRange.end.getTime()) {
      return adapterErr({
        code: "invalid_request",
        message:
          "Requested Facebook date range is outside the supported retention window.",
        retryable: false,
      });
    }

    if (clamped) {
      logFacebookMetricsEvent(ctx, {
        event: "fb_pages_date_clamped",
        pageId,
        tenantId: ctx.tenantId,
        originalStart: dateRange.start.toISOString(),
        clampedTo: new Date(since * 1000).toISOString(),
      });
    }

    // Fetch day-period insights — batch 1 (core + reactions)
    const authHeaders = { headers: { Authorization: `Bearer ${pageAccessToken}` },
    };
    let rateLimitWarning = false;

    const batch1Url = `${META_GRAPH_BASE}/${pageId}/insights?metric=${DAY_PERIOD_METRICS_BATCH_1.join(",")}&period=day&since=${since}&until=${until}`;
    const batch1Result = await safeFetch(batch1Url, authHeaders, 25_000);

    if (!batch1Result.success) {
      return handleMetaFetchError(batch1Result);
    }

    const batch1Response = batch1Result.data;
    const appUsage = parseAppUsage(batch1Response);
    if (appUsage > RATE_LIMIT_THRESHOLD) {
      rateLimitWarning = true;
      logFacebookMetricsEvent(ctx, {
        event: "meta_rate_limit_approaching",
        callCount: appUsage,
        pageId,
        tenantId: ctx.tenantId,
      });
    }

    let batch1Data: MetaInsightsResponse;
    try {
      batch1Data = (await batch1Response.json()) as MetaInsightsResponse;
    } catch (err) {
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(
          `Failed to parse Facebook Insights response: ${err instanceof Error ? err.message : String(err)}`,
        ),
        retryable: false,
      });
    }

    // Check for API-level error in the JSON body
    if (batch1Data.error) {
      const metaError = parseMetaGraphError(JSON.stringify(batch1Data));
      if (metaError) {
        return adapterErr({
          code: metaError.code,
          message: sanitizeErrorMessage(metaError.message),
          retryable: metaError.retryable,
        });
      }
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(batch1Data.error.message),
        retryable: false,
      });
    }

    // Parse batch 1 into daily records
    const dailyMap = parseInsightsResponse(batch1Data.data);

    // Fetch day-period insights — batch 2 (video splits)
    if (!rateLimitWarning) {
      const batch2Url = `${META_GRAPH_BASE}/${pageId}/insights?metric=${DAY_PERIOD_METRICS_BATCH_2.join(",")}&period=day&since=${since}&until=${until}`;
      const batch2Result = await safeFetch(batch2Url, authHeaders, 25_000);

      if (batch2Result.success) {
        const batch2AppUsage = parseAppUsage(batch2Result.data);
        if (batch2AppUsage > RATE_LIMIT_THRESHOLD) {
          rateLimitWarning = true;
        }

        try {
          const batch2Data = (await batch2Result.data.json()) as MetaInsightsResponse;
          if (!batch2Data.error && batch2Data.data) {
            // Merge batch 2 data into dailyMap
            const batch2Map = parseInsightsResponse(batch2Data.data);
            for (const [dateStr, metrics] of batch2Map) {
              if (!dailyMap.has(dateStr)) {
                dailyMap.set(dateStr, {});
              }
              Object.assign(dailyMap.get(dateStr)!, metrics);
            }
          }
        } catch {
          // Non-fatal — video splits are supplementary
          logFacebookMetricsEvent(ctx, {
            event: "fb_pages_video_splits_parse_warning",
            pageId,
            tenantId: ctx.tenantId,
          });
        }
      }
    }

    // Fetch lifetime metrics (page_follows, page_lifetime_engaged_followers_unique)
    let latestFollowers = 0;
    let latestEngagedFollowers = 0;
    if (!rateLimitWarning) {
      const lifetimeUrl = `${META_GRAPH_BASE}/${pageId}/insights?metric=${LIFETIME_METRICS.join(",")}&period=day&since=${since}&until=${until}`;
      const lifetimeResult = await safeFetch(lifetimeUrl, authHeaders, 25_000);

      if (lifetimeResult.success) {
        const lifetimeAppUsage = parseAppUsage(lifetimeResult.data);
        if (lifetimeAppUsage > RATE_LIMIT_THRESHOLD) {
          rateLimitWarning = true;
        }

        try {
          const lifetimeData = (await lifetimeResult.data.json()) as MetaInsightsResponse;
          if (lifetimeData.data) {
            for (const metric of lifetimeData.data) {
              if (metric.values.length === 0) continue;

              // Store the most recent value as a fallback
              const lastValue = metric.values[metric.values.length - 1];
              if (lastValue && typeof lastValue.value === "number") {
                if (metric.name === "page_follows") {
                  latestFollowers = lastValue.value;
                } else if (metric.name === "page_lifetime_engaged_followers_unique") {
                  latestEngagedFollowers = lastValue.value;
                }
              }

              // Add all values to daily map
              for (const val of metric.values) {
                const dateStr = val.end_time.split("T")[0];
                if (!dateStr) continue;
                if (!dailyMap.has(dateStr)) {
                  dailyMap.set(dateStr, {});
                }
                if (typeof val.value === "number") {
                  dailyMap.get(dateStr)![metric.name] = val.value;
                }
              }
            }
          }
        } catch {
          // Non-fatal — lifetime metrics are supplementary
          logFacebookMetricsEvent(ctx, {
            event: "fb_pages_lifetime_parse_warning",
            pageId,
            tenantId: ctx.tenantId,
          });
        }
      } else {
        // Non-fatal — log and continue with day metrics
        logFacebookMetricsEvent(ctx, {
          event: "fb_pages_lifetime_fetch_warning",
          pageId,
          tenantId: ctx.tenantId,
          errorCode: lifetimeResult.error.code,
        });
      }
    }

    // Build dailySummary records
    const records: Record<string, unknown>[] = [];
    for (const [dateStr, metrics] of dailyMap) {
      const uniqueViewers = metrics.page_total_media_view_unique ?? 0;
      const engagements = metrics.page_post_engagements ?? 0;
      const engagementRate = uniqueViewers > 0 ? engagements / uniqueViewers : 0;

      records.push({
        _reportType: "dailySummary",
        date: dateStr,
        day_of_week: getDayOfWeek(dateStr),
        // Core metrics
        page_follows: metrics.page_follows ?? latestFollowers,
        page_daily_follows: metrics.page_daily_follows ?? 0,
        page_daily_unfollows: metrics.page_daily_unfollows ?? 0,
        page_media_view: metrics.page_media_view ?? 0,
        page_total_media_view_unique: uniqueViewers,
        page_views_total: metrics.page_views_total ?? 0,
        page_post_engagements: engagements,
        page_video_views: metrics.page_video_views ?? 0,
        engagement_rate: engagementRate,
        // Reaction metrics
        page_actions_post_reactions_like_total: metrics.page_actions_post_reactions_like_total ?? 0,
        page_actions_post_reactions_love_total: metrics.page_actions_post_reactions_love_total ?? 0,
        page_actions_post_reactions_wow_total: metrics.page_actions_post_reactions_wow_total ?? 0,
        page_actions_post_reactions_haha_total: metrics.page_actions_post_reactions_haha_total ?? 0,
        page_actions_post_reactions_sorry_total: metrics.page_actions_post_reactions_sorry_total ?? 0,
        page_actions_post_reactions_anger_total: metrics.page_actions_post_reactions_anger_total ?? 0,
        page_actions_post_reactions_total: metrics.page_actions_post_reactions_total ?? 0,
        page_total_actions: metrics.page_total_actions ?? 0,
        // Video metric splits
        page_video_views_paid: metrics.page_video_views_paid ?? 0,
        page_video_views_organic: metrics.page_video_views_organic ?? 0,
        page_video_views_autoplayed: metrics.page_video_views_autoplayed ?? 0,
        page_video_views_click_to_play: metrics.page_video_views_click_to_play ?? 0,
        page_video_complete_views_30s: metrics.page_video_complete_views_30s ?? 0,
        page_video_complete_views_30s_paid: metrics.page_video_complete_views_30s_paid ?? 0,
        page_video_complete_views_30s_organic: metrics.page_video_complete_views_30s_organic ?? 0,
        page_video_complete_views_30s_autoplayed: metrics.page_video_complete_views_30s_autoplayed ?? 0,
        page_video_complete_views_30s_click_to_play: metrics.page_video_complete_views_30s_click_to_play ?? 0,
        page_video_repeat_views: metrics.page_video_repeat_views ?? 0,
        page_video_view_time: metrics.page_video_view_time ?? 0,
        // Paid vs organic combined breakdown (sum of paid + unpaid from object value)
        page_video_views_by_paid_non_paid: metrics.page_video_views_by_paid_non_paid ?? 0,
        // Follower split
        page_fan_adds_by_paid_non_paid_unique: metrics.page_fan_adds_by_paid_non_paid_unique ?? 0,
        // Lifetime metrics
        page_lifetime_engaged_followers_unique: metrics.page_lifetime_engaged_followers_unique ?? latestEngagedFollowers,
      });
    }

    // Sort by date
    records.sort((a, b) => String(a.date).localeCompare(String(b.date)));

    // Fetch post-level data for Top Posts widget (if rate limit allows)
    if (!rateLimitWarning) {
      const postsUrl = `${META_GRAPH_BASE}/${pageId}/published_posts?fields=id,message,created_time,full_picture,permalink_url,type,shares,reactions.summary(true),comments.summary(true)&since=${since}&until=${until}&limit=25`;

      const postsResult = await safeFetch(postsUrl, authHeaders, 25_000);

      if (postsResult.success) {
        const postsAppUsage = parseAppUsage(postsResult.data);
        if (postsAppUsage > RATE_LIMIT_THRESHOLD) {
          rateLimitWarning = true;
        }

        try {
          const postsData = (await postsResult.data.json()) as MetaPostsResponse;

          if (postsData.data) {
            for (const post of postsData.data) {
              const reactions = post.reactions?.summary?.total_count ?? 0;
              const comments = post.comments?.summary?.total_count ?? 0;
              const shares = post.shares?.count ?? 0;
              const postDate = post.created_time.split("T")[0] ?? "";

              // Determine if this is a video post from the `type` field returned by Graph API.
              // Note: Reels also return type="video" in Meta's data model.
              const isVideoPost = post.type === "video";

              // Build per-post insights record
              const postRecord: Record<string, unknown> = {
                _reportType: "posts",
                _date: postDate,
                date: postDate,
                postId: post.id,
                postType: post.type ?? "status",
                message: post.message ?? "",
                imageUrl: post.full_picture ?? null,
                permalinkUrl: post.permalink_url ?? null,
                createdTime: post.created_time,
                reactions: reactions,
                comments: comments,
                shares: shares,
                totalEngagement: reactions + comments + shares,
              };

              // Fetch per-post insights (rate-limit guarded)
              if (!rateLimitWarning) {
                const basePostMetrics = "post_media_views,post_total_media_view_unique,post_clicks,post_clicks_by_type,post_reactions_by_type_total,post_media_views_paid,post_media_views_organic,post_media_views_follower,post_activity_by_action_type";
                const videoPostMetrics = isVideoPost ? ",post_video_views,post_video_complete_views_30s,post_video_avg_time_watched,post_video_view_time" : "";
                const postInsightsUrl = `${META_GRAPH_BASE}/${post.id}/insights?metric=${basePostMetrics}${videoPostMetrics}`;

                const postInsightsResult = await safeFetch(postInsightsUrl, authHeaders, 10_000,
                );

                if (postInsightsResult.success) {
                  const piAppUsage = parseAppUsage(postInsightsResult.data);
                  if (piAppUsage > RATE_LIMIT_THRESHOLD) {
                    rateLimitWarning = true;
                  }

                  try {
                    const piData = (await postInsightsResult.data.json()) as MetaPostInsightsResponse;
                    if (piData.data) {
                      for (const insight of piData.data) {
                        const val = insight.values?.[0]?.value;
                        if (val === undefined) continue;

                        switch (insight.name) {
                          case "post_media_views":
                            postRecord.views = typeof val === "number" ? val : 0;
                            break;
                          case "post_total_media_view_unique":
                            postRecord.unique_viewers = typeof val === "number" ? val : 0;
                            break;
                          case "post_clicks":
                            postRecord.clicks = typeof val === "number" ? val : 0;
                            break;
                          case "post_clicks_by_type":
                            postRecord.clicks_by_type = typeof val === "object" ? val : {};
                            break;
                          case "post_reactions_by_type_total":
                            postRecord.reactions_by_type = typeof val === "object" ? val : {};
                            break;
                          case "post_video_views":
                            postRecord.video_views = typeof val === "number" ? val : 0;
                            break;
                          case "post_video_complete_views_30s":
                            postRecord.video_30s_complete = typeof val === "number" ? val : 0;
                            break;
                          case "post_video_avg_time_watched":
                            postRecord.video_avg_watch_time = typeof val === "number" ? val : 0;
                            break;
                          case "post_video_view_time":
                            postRecord.video_view_time = typeof val === "number" ? val : 0;
                            break;
                          case "post_media_views_paid":
                            postRecord.views_paid = typeof val === "number" ? val : 0;
                            break;
                          case "post_media_views_organic":
                            postRecord.views_organic = typeof val === "number" ? val : 0;
                            break;
                          case "post_media_views_follower":
                            postRecord.views_follower = typeof val === "number" ? val : 0;
                            break;
                          case "post_activity_by_action_type":
                            postRecord.activity_by_type = typeof val === "object" ? val : {};
                            break;
                        }
                      }
                    }
                  } catch {
                    // Non-fatal — per-post insights are supplementary
                    logFacebookMetricsEvent(ctx, {
                      event: "fb_pages_post_insights_parse_warning",
                      postId: post.id,
                      pageId,
                      tenantId: ctx.tenantId,
                    });
                  }
                }
              }

              // Flatten breakdown objects into individual numeric fields (default to 0 if not fetched)
              const clicksByType = postRecord.clicks_by_type as
                | Record<string, number> | undefined;
              postRecord.post_clicks_link =
                clicksByType && typeof clicksByType === "object"
                  ? (clicksByType.link_clicks ?? 0) : 0;
              postRecord.post_clicks_photo_view =
                clicksByType && typeof clicksByType === "object"
                  ? (clicksByType.photo_view ?? 0) : 0;
              postRecord.post_clicks_other =
                clicksByType && typeof clicksByType === "object"
                  ? (clicksByType.other ?? clicksByType.other_clicks ?? 0) : 0;

              const reactionsByType = postRecord.reactions_by_type as
                | Record<string, number> | undefined;
              postRecord.post_reactions_like =
                reactionsByType && typeof reactionsByType === "object"
                  ? (reactionsByType.like ?? 0) : 0;
              postRecord.post_reactions_love =
                reactionsByType && typeof reactionsByType === "object"
                  ? (reactionsByType.love ?? 0) : 0;
              postRecord.post_reactions_wow =
                reactionsByType && typeof reactionsByType === "object"
                  ? (reactionsByType.wow ?? 0) : 0;
              postRecord.post_reactions_haha =
                reactionsByType && typeof reactionsByType === "object"
                  ? (reactionsByType.haha ?? 0) : 0;
              postRecord.post_reactions_sorry =
                reactionsByType && typeof reactionsByType === "object"
                  ? (reactionsByType.sorry ?? reactionsByType.sad ?? 0) : 0;
              postRecord.post_reactions_anger =
                reactionsByType && typeof reactionsByType === "object"
                  ? (reactionsByType.anger ?? reactionsByType.angry ?? 0) : 0;

              const activityByType = postRecord.activity_by_type as
                | Record<string, number> | undefined;
              postRecord.post_activity_like =
                activityByType && typeof activityByType === "object"
                  ? (activityByType.like ?? 0) : 0;
              postRecord.post_activity_comment =
                activityByType && typeof activityByType === "object"
                  ? (activityByType.comment ?? 0) : 0;
              postRecord.post_activity_share =
                activityByType && typeof activityByType === "object"
                  ? (activityByType.share ?? 0) : 0;

              records.push(postRecord);

              // Generate postReactions reportType record (AC #2)
              if (reactionsByType && typeof reactionsByType === "object") {
                records.push({
                  _reportType: "postReactions",
                  _date: postDate,
                  date: postDate,
                  postId: post.id,
                  post_reactions_like: reactionsByType.like ?? 0,
                  post_reactions_love: reactionsByType.love ?? 0,
                  post_reactions_wow: reactionsByType.wow ?? 0,
                  post_reactions_haha: reactionsByType.haha ?? 0,
                  post_reactions_sorry: reactionsByType.sorry ?? reactionsByType.sad ?? 0,
                  post_reactions_anger: reactionsByType.anger ?? reactionsByType.angry ?? 0,
                });
              }
            }
          }
        } catch {
          // Non-fatal — post data is supplementary
          logFacebookMetricsEvent(ctx, {
            event: "fb_pages_posts_parse_warning",
            pageId,
            tenantId: ctx.tenantId,
          });
        }
      }
    }

    // Derive per-day comment and share totals from post records (AC #3)
    // Group posts by date so each dailySummary gets its own day's totals
    const postsByDate = new Map<string, { comments: number; shares: number }>();
    for (const r of records) {
      if (r._reportType === "posts") {
        const d = String(r.date);
        const entry = postsByDate.get(d) ?? { comments: 0, shares: 0 };
        entry.comments += typeof r.comments === "number" ? r.comments : 0;
        entry.shares += typeof r.shares === "number" ? r.shares : 0;
        postsByDate.set(d, entry);
      }
    }
    // Set per-day totals on dailySummary records; days without posts get 0
    for (const record of records) {
      if (record._reportType === "dailySummary") {
        const dayData = postsByDate.get(String(record.date));
        record.comments_total = dayData?.comments ?? 0;
        record.shares_total = dayData?.shares ?? 0;
      }
    }

    return adapterOk({
      platform: "facebook-pages",
      accountId: pageId,
      dateRange: { start: effectiveStart, end: dateRange.end },
      records,
      fetchedAt: new Date(),
      recordCount: records.length,
    });
  },

  // --------------------------------------------------------------------------
  // validateConnection (Subtask 1.5)
  // --------------------------------------------------------------------------
  async validateConnection(
    ctx: AdapterContext,
    _credentials: OAuthCredentials,
  ): Promise<AdapterResult<boolean>> {
    const pageId = ctx.accountId;
    if (!pageId) {
      return adapterErr({
        code: "account_not_found",
        message: "No Facebook Page ID provided for connection validation.",
        retryable: false,
      });
    }

    const env = ctx.env as {
      KV: unknown;
      ENCRYPTION_KEY?: string;
      oauthTokenId?: string;
      resolveCertificationPageAccessToken?: CertificationPageAccessTokenResolver;
    };

    let pageAccessToken: string;
    if (ctx.certification?.mode === "live-evidence") {
      if (!env.resolveCertificationPageAccessToken) {
        return adapterErr({
          code: "invalid_response",
          message: "Facebook Pages certification token capability not provided",
          retryable: false,
        });
      }

      const tokenResult = await env.resolveCertificationPageAccessToken();
      if (!tokenResult.success) {
        return adapterErr(tokenResult.error);
      }
      pageAccessToken = tokenResult.data.pageAccessToken;
    } else {
      if (!env.oauthTokenId || !env.ENCRYPTION_KEY) {
      return adapterErr({
        code: "invalid_response",
        message: "Missing oauthTokenId or ENCRYPTION_KEY for validation",
        retryable: false,
      });
    }

    const tokenResult = await getPageAccessToken(
      env.KV,
      env.oauthTokenId,
      pageId,
      ctx.tenantId,
      env.ENCRYPTION_KEY,
      );

    if (!tokenResult.success) {
      return adapterErr(tokenResult.error);
    }
      pageAccessToken = tokenResult.data.pageAccessToken;
    }
    const url = `${META_GRAPH_BASE}/${pageId}?fields=id,name`;
    const result = await safeFetch(url, { headers: { Authorization: `Bearer ${pageAccessToken}` } }, 5_000,
    );

    if (!result.success) {
      return handleMetaFetchError(result);
    }

    return adapterOk(true);
  },

  // --------------------------------------------------------------------------
  // discoverAccounts (Subtask 1.6)
  // --------------------------------------------------------------------------
  async discoverAccounts(
    _ctx: AdapterContext,
    credentials: OAuthCredentials,
  ): Promise<AdapterResult<AccountInfo[]>> {
    // Use the USER token (not page token) for discovery.
    // Story 31.20: fetch ONLY the pages section (AC #1) and require it to be
    // complete (AC #3) — a truncated catalog must never reach the
    // grant-resource upsert as success.
    const discoveryResult = await discoverMetaAccounts({
      accessToken: credentials.accessToken,
      grantedScopes: credentials.scopes,
      sections: { pages: true, adAccounts: false, instagram: false },
    });

    const pagesStatus = discoveryResult.sectionStatus.pages;
    if (pagesStatus !== "complete") {
      return mapDiscoverySectionFailure(pagesStatus, discoveryResult.firstError,
      );
    }

    // Defensive dedupe by id, first occurrence wins (ADR-0004 §Anti-Patterns 4).
    const accountsById = new Map<string, AccountInfo>();
    for (const page of discoveryResult.pages) {
      if (accountsById.has(page.id)) continue;
      // SECURITY: page.accessToken is intentionally dropped — catalog rows
      // are DB-persisted and must NEVER carry token material (AC #7 pin).
      accountsById.set(page.id, {
        id: page.id,
        name: page.name,
        platform: "facebook-pages" as const,
        metadata: { category: page.category },
        // Fresh spread per row — display/consent metadata only (Story 31-4)
        scopes: [...META_INTEGRATION_SCOPES["facebook-pages"].scopes],
      });
    }

    return adapterOk([...accountsById.values()]);
  },

  // --------------------------------------------------------------------------
  // getAvailableMetrics (Subtask 1.2)
  // --------------------------------------------------------------------------
  getAvailableMetrics(): MetricDefinition[] {
    return FB_PAGES_METRICS;
  },

  // --------------------------------------------------------------------------
  // getAvailableDimensions (Subtask 1.3)
  // --------------------------------------------------------------------------
  getAvailableDimensions(): DimensionDefinition[] {
    return FB_PAGES_DIMENSIONS;
  },
};
